import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, TrendingUp, Search, ArrowDown, ArrowUp, Loader2, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const money = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace('.', ',')}`;

/** Modal listing every user with their financial aggregates + filters/sorts. */
const HistoricoGeralModal = ({ open, onClose, apiHeaders }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [year, setYear]   = useState('');
  const [month, setMonth] = useState('');
  const [iaFilter, setIaFilter] = useState('all'); // 'all' | 'dubla' | 'nao'
  const [sortKey, setSortKey] = useState('faturamento_total');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year)  params.append('year',  year);
      if (month) params.append('month', month);
      const res = await axios.get(`${API}/finance-dashboard/historico-geral?${params}`, { headers: apiHeaders });
      setEntries(res.data?.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar histórico geral');
    } finally { setLoading(false); }
  }, [year, month, apiHeaders]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = entries.filter(e => {
      if (iaFilter === 'dubla' && !e.dubla_ia) return false;
      if (iaFilter === 'nao'   &&  e.dubla_ia) return false;
      if (!q) return true;
      return (e.user_name || '').toLowerCase().includes(q)
          || (e.email || '').toLowerCase().includes(q);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (typeof av === 'string') return av.localeCompare(bv || '') * dir;
      return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
    });
    return arr;
  }, [entries, search, iaFilter, sortKey, sortDir]);

  const totals = useMemo(() => {
    return filtered.reduce((s, e) => ({
      a_receber: s.a_receber + (e.a_receber || 0),
      pago:      s.pago      + (e.pago || 0),
      total:     s.total     + (e.faturamento_total || 0),
    }), { a_receber: 0, pago: 0, total: 0 });
  }, [filtered]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortHeader = ({ label, k, align = 'right' }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2 text-${align} text-xs uppercase tracking-wider text-slate-300 cursor-pointer hover:text-white select-none whitespace-nowrap`}
      data-testid={`historico-geral-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );

  const uniqueYears = useMemo(() => {
    // Derive from current filter set — we hint recent years so admin can pick fast.
    const y = new Date().getFullYear();
    return [y - 1, y, y + 1];
  }, []);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="historico-geral-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-700 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-teal-400" /> Histórico Geral
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Visão agregada por pessoa: valores a receber, pagos e faturamento total. Filtre por período e ordene por qualquer coluna.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="historico-geral-close">
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Filtros */}
        <div className="px-6 py-3 border-b border-slate-800 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar nome ou email…"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
              data-testid="historico-geral-search"
            />
          </div>
          <select value={month} onChange={e => setMonth(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
            data-testid="historico-geral-month">
            <option value="">Todos os meses</option>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
            data-testid="historico-geral-year">
            <option value="">Todos os anos</option>
            {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={iaFilter} onChange={e => setIaFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
            data-testid="historico-geral-ia">
            <option value="all">Todos (IA e não-IA)</option>
            <option value="dubla">Só quem DUBLA IA</option>
            <option value="nao">Só quem NÃO dubla IA</option>
          </select>
        </div>

        {/* Totais */}
        <div className="px-6 py-3 border-b border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-amber-300">A Receber</p>
            <p className="text-xl font-mono font-bold text-white mt-1" data-testid="historico-geral-total-a-receber">{money(totals.a_receber)}</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300">Pago</p>
            <p className="text-xl font-mono font-bold text-white mt-1" data-testid="historico-geral-total-pago">{money(totals.pago)}</p>
          </div>
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-teal-300">Faturamento Total</p>
            <p className="text-xl font-mono font-bold text-white mt-1" data-testid="historico-geral-total-geral">{money(totals.total)}</p>
          </div>
        </div>

        {/* Tabela */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">Nenhum registro encontrado.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-800/60 sticky top-0 z-10">
                <tr>
                  <SortHeader label="Nome"      k="user_name"  align="left" />
                  <SortHeader label="Email"     k="email"      align="left" />
                  <SortHeader label="Dubla IA"  k="dubla_ia"   align="center" />
                  <SortHeader label="A receber" k="a_receber" />
                  <SortHeader label="Pago"      k="pago" />
                  <SortHeader label="Total"     k="faturamento_total" />
                  <SortHeader label="Projetos"  k="projects_count" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.user_id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`historico-geral-row-${e.user_id}`}>
                    <td className="px-3 py-2 text-white font-medium">{e.user_name || '—'}</td>
                    <td className="px-3 py-2 text-slate-300 text-xs">{e.email || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {e.dubla_ia ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
                          <Sparkles className="h-3 w-3" /> IA
                        </span>
                      ) : <span className="text-slate-600 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-amber-300">{money(e.a_receber)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-300">{money(e.pago)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-white">{money(e.faturamento_total)}</td>
                    <td className="px-3 py-2 text-right text-slate-400">{e.projects_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoricoGeralModal;
